import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { DocumentSelectOption, Dokument, Eigenleistung, EigenleistungAufstellung, GenerationData, Partnerfirma, SelectOption, Zahlungsempfaenger } from '@gsm/entities';
import { DynamicFormControlEvent, DynamicFormGroupModel, DynamicFormModel, DynamicFormValueControlModel, DynamicSelectModel } from '@ng-dynamic-forms/core';
import { DynamicFormControlModel } from '@ng-dynamic-forms/core/lib/model/dynamic-form-control.model';
import { DynamicMaterialFormComponent } from '@ng-dynamic-forms/ui-material';
import { CustomDynamicFormService } from '../../custom-dynamic-form.service';
import { getDisabledIds, setModelDisabled } from '../../helpers/disable-model.helper';
import { filterEmptyProps } from '../../helpers/filter-props.helper';
import { formElementsInvalid } from '../../helpers/form-elements-invalid.helper';
import { getGroupValue } from '../../helpers/get-group-value.helper';
import { CustomDynamicFormControlComponent } from '../custom-dynamic-form-control.component';
import { CustomFileModel } from '../custom-file-upload/custom-file-upload.model';
import { DokumentenAngabeModel } from '../dokumenten-angabe/dokumenten-angabe.model';
import { AnyFormControlModel } from '../shared-models/any-form-control.model';
import { GEBAEUDE_SCHADEN_TEMPLATE_FACTORY } from './gebaeude-schaden.config';
import { GebaeudeSchadenModel } from './gebaeude-schaden.model';

export const EIGENLEISTUNGSAUFSTELLUNG_DOK_TYP: DocumentSelectOption = {
  label:                      'Eigenleistungsaufstellung',
  value:                      'EIGEN_AU',
  zahlungsempfaengerRequired: true
};

@Component({
  selector:    'gsm-gebaeude-schaden',
  templateUrl: './gebaeude-schaden.component.html',
  styleUrls:   ['./gebaeude-schaden.component.scss'],
  imports:     [
    DynamicMaterialFormComponent
  ],
  standalone:  true
})
export class GebaeudeSchadenComponent extends CustomDynamicFormControlComponent<GebaeudeSchadenModel> implements OnInit {

  private formService = inject(CustomDynamicFormService);

  @ViewChild('form', {static: true}) formComponent: DynamicMaterialFormComponent;

  formModel: DynamicFormModel;
  formGroup: UntypedFormGroup;

  private _cachedModels: Record<string, any>;
  private _disabledIds: string[] = [];
  private _isUpdating = false;

  constructor() {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  protected onModelChange(): void {
    const {
      docTypEigenleistung,
      docTypFachfirma,
      gewerkeList,
      kundeList,
      schadenhoeheList,
      updateClaim
    } =
    this.model.additional || ({} as any);

    let removeRechnungssteller = true;
    if (this.model.additional.removeRechnungssteller !== undefined) {
      removeRechnungssteller = this.model.additional.removeRechnungssteller;
    }
    const json = GEBAEUDE_SCHADEN_TEMPLATE_FACTORY(
      gewerkeList,
      kundeList,
      docTypEigenleistung,
      docTypFachfirma,
      schadenhoeheList,
      updateClaim,
      removeRechnungssteller
    );
    this._disabledIds = getDisabledIds(json);

    if (this.model.disabled) {
      setModelDisabled(json, true);
    }

    this.formModel = this.formService.fromJSON(json);
    this.formGroup = this.formService.createFormGroup(this.formModel);

    this._cachedModels = {};

    this._updateFormFromValue();
    this._emitChange();
  }

  protected onValueChange(): void {
    if (!this._isUpdating) {
      this._updateFormFromValue();
      this._emitChange();
    }
  }

  protected onDisabledChange(): void {
    setModelDisabled(this.formModel, this.model.disabled, {excludeIds: this._disabledIds});
  }

  onFormChange(event: DynamicFormControlEvent): void {
    if (!this._isUpdating) {
      if (event.model.id === 'schadenbehebung') {
        this._onSchadenBehebungChange(event.model as DynamicSelectModel<SelectOption>);
        this.formGroup.markAllAsTouched();
      }
      this._updateValueFromForm();
      if (event.model.id === 'gewerke' || event.model.id === 'eigenleistungAufstellung' || event.model.id === 'vorhanden') {
        this._updateEigenleistungGewerkeFromValue();
      }
      this._emitChange();
    }
  }

  private _onSchadenBehebungChange(model: DynamicSelectModel<SelectOption>) {
    // If partnerfirma and/or fachfirma are selected, check if they have empty values,
    // and if so, add a single blank default entry.
    [
      [
        'partnerfirma',
        'partnerfirmaList'
      ],
      [
        'fachfirma',
        'fachfirmaList'
      ]
    ]
      .filter(([name]) => (model.value as SelectOption[])?.some(selectedVal => selectedVal.value && selectedVal.value === name))
      .map(([, modelId]) => this._findModelById(modelId))
      .forEach((listModel: DynamicFormValueControlModel<any[]>) => {
        if (!listModel.value?.length) {
          listModel.value = [{}];
        }
      });
  }

  private _updateEigenleistungGewerkeFromValue() {
    const belegeModel: DokumentenAngabeModel = this._findModelById('belege');
    belegeModel.additional.gewerke = this.model.value?.eigenleistung?.aufstellung?.gewerke || [];
  }

  private _emitChange() {
    this.group.get(this.model.id)
      .setErrors(this._getErrors());
    this.change.emit(this.model.value);
  }

  private _getErrors(): ValidationErrors {
    const invalid = formElementsInvalid(this.formGroup, this.formModel);
    return invalid ? {gebaeudeSchaden: true} : null;
  }

  private _updateFormFromValue() {
    if (!this.model.value) {
      return;
    }

    // ignore form change events while this update is happening
    this._isUpdating = true;

    this._updateEigenleistungGewerkeFromValue();
    const {
      massnahmenBekannt,
      schadenbehebung,
      partnerfirmaList,
      eigenleistung,
      fachfirmaList,
      gesamtschadenhoehe
    } = this.model.value;

    this.formGroup.get('gebaeude_schaden_group')
      .patchValue({
        massnahmenBekannt,
        schadenbehebung,
        partnerfirma_group:       {partnerfirmaList},
        eigenleistung_group:      this._getGroupValueFromEigenleistung(eigenleistung || {}),
        fachfirma_group:          {fachfirmaList},
        gesamtschadenhoehe_group: {gesamtschadenhoehe}
      });

    this._updateUebersichtFromValue();
    this._isUpdating = false;
  }

  private _updateValueFromForm() {
    this._isUpdating = true;

    const {
      gebaeude_schaden_group: {
                                massnahmenBekannt = null as boolean,
                                schadenbehebung = null as SelectOption[],
                                partnerfirma_group: {partnerfirmaList} = {} as { partnerfirmaList: Partnerfirma[] },
                                eigenleistung_group = {} as EigenleistungGroupValue,
                                fachfirma_group:          {fachfirmaList} = {} as { fachfirmaList: Dokument[] },
                                gesamtschadenhoehe_group: {gesamtschadenhoehe} = {} as {
                                  gesamtschadenhoehe: SelectOption
                                }
                              }
    } = getGroupValue(this.formModel);

    const eigenleistung = this._getEigenleistungFromGroupValue(eigenleistung_group);

    this.model.value = filterEmptyProps(
      {
        massnahmenBekannt,
        schadenbehebung,
        partnerfirmaList,
        eigenleistung,
        fachfirmaList,
        gesamtschadenhoehe
      },
      true
    );

    this._updateUebersichtFromValue();

    this._isUpdating = false;
  }

  private _getEigenleistungFromGroupValue(eigenleistungGroupValue: EigenleistungGroupValue): Eigenleistung {
    const {
      vorhanden,
      checklistItemFileList,
      gewerke,
      eigenleistungAufstellung,
      zahlungsempfaenger,
      belege
    } = eigenleistungGroupValue;

    if (!vorhanden) {
      return null;
    }

    if (vorhanden === 'N') {
      return {
        vorhanden,
        aufstellung: {
          vorhanden,
          typ: EIGENLEISTUNGSAUFSTELLUNG_DOK_TYP
        }
      };
    }

    const aufstellung: Dokument =
      vorhanden === 'V'
        ? {
          vorhanden: 'V',
          typ:       EIGENLEISTUNGSAUFSTELLUNG_DOK_TYP,
          checklistItemFileList,
          gewerke,
          zahlungsempfaenger
        }
        : // otherwise vorhanden === 'A'
        {
          vorhanden:      'V', // inside the dokument it's considered vorhanden
          typ:            EIGENLEISTUNGSAUFSTELLUNG_DOK_TYP,
          generationData: eigenleistungAufstellung ? {eigenleistungAufstellung} : null,
          gewerke:        eigenleistungAufstellung?.positionen
                            ? eigenleistungAufstellung.positionen.map(item => item.gewerk)
              .filter(gewerk => !!gewerk)
                            : null,
          zahlungsempfaenger
        };

    return filterEmptyProps(
      {
        vorhanden,
        aufstellung: filterEmptyProps(aufstellung, true),
        belege
      },
      true
    );
  }

  private _getGroupValueFromEigenleistung(eigenleistung: Eigenleistung): EigenleistungGroupValue {
    const {
      vorhanden,
      aufstellung: {
                     checklistItemFileList,
                     generationData: {eigenleistungAufstellung} = {} as GenerationData,
                     gewerke,
                     zahlungsempfaenger
                   } = {} as Dokument,
      belege
    } = eigenleistung;

    return {
      vorhanden,
      checklistItemFileList,
      gewerke,
      eigenleistungAufstellung,
      zahlungsempfaenger,
      belege
    };
  }

  private _updateUebersichtFromValue() {
    if (!this.model.value) {
      return;
    }

    const {
      partnerfirmaList,
      eigenleistung,
      fachfirmaList
    } = this.model.value;

    // update uebersicht
    const uebersicht = {
      uebersicht_partnerfirma:  partnerfirmaList ? partnerfirmaList.map(pf => pf.gewerk)
        .filter(gewerk => !!gewerk) : [],
      uebersicht_eigenleistung: eigenleistung?.aufstellung?.gewerke || [],
      uebersicht_fachfirma:     fachfirmaList
                                  ? fachfirmaList.reduce((gewerke, dok) => (dok.gewerke ? [
          ...gewerke,
          ...dok.gewerke
        ] : gewerke), [])
                                  : []
    };
    this.formGroup.get('gebaeude_schaden_group.uebersicht_zur_ermittlung')
      .setValue(uebersicht);

    const uebersichtModel: DynamicFormGroupModel = this._findModelById('uebersicht_zur_ermittlung');

    // set parts of uebersicht to hidden where no entries are present
    uebersichtModel.group.forEach((model: AnyFormControlModel) => (model.hidden = !model.value || !model.value.length));
    uebersichtModel.hidden = uebersichtModel.group.every(model => model.hidden);

    this.formService.detectChanges(this.formComponent);
  }

  // finds models by ID using form service, and caches them for performance
  private _findModelById<T extends DynamicFormControlModel>(id: string): T | null {
    return this._cachedModels[id] || (this._cachedModels[id] = this.formService.findModelById(id, this.formModel));
  }
}

type EigenleistungGroupValue = {
  vorhanden: string;
  checklistItemFileList: CustomFileModel[];
  gewerke: SelectOption[];
  eigenleistungAufstellung: EigenleistungAufstellung;
  zahlungsempfaenger: Zahlungsempfaenger;
  belege: Dokument[];
};
